import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { hostname } from "../../../utils";
import { BrowserIntegrationParameters } from "../../../types";

interface LinkedInLoginIntegrationFormProps {
  editMode: boolean;
  initialData?: {
    email?: string;
    name?: string;
    parameters?: BrowserIntegrationParameters;
  };
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: any) => void;
  getToken: () => Promise<string>;
  companyId: string;
}

export default function LinkedInLoginIntegrationForm({
  editMode,
  initialData = {},
  onValidationChange,
  onDataChange,
  getToken,
  companyId,
}: LinkedInLoginIntegrationFormProps) {
  const [email, setEmail] = useState(initialData.email || "");
  const [zipCode, setZipCode] = useState("");

  // Browserbase session state
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [liveUrl, setLiveUrl] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] = useState<'idle' | 'creating' | 'ready' | 'saving' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState("");
  // Additional session data needed for saving (returned from create endpoint)
  const [sessionData, setSessionData] = useState<{
    contextId?: string;
    zipCode?: string;
    os?: string;
    fingerprint?: any;
    userAgent?: string;
    startUrl?: string;
  }>({});

  // Validation effect
  useEffect(() => {
    let isValid = false;

    if (editMode) {
      // In edit mode, form is always valid since we just show info
      isValid = true;
    } else {
      // In create mode, need session to be saved successfully
      isValid = sessionStatus === 'success';
    }

    onValidationChange(isValid);

    // Update parent with form data when successful
    if (sessionStatus === 'success' && sessionId) {
      onDataChange({
        sessionId,
        email,
        // Include session data for multi-instance compatibility
        ...sessionData,
      });
    }
  }, [sessionStatus, sessionId, email, editMode, sessionData, onValidationChange, onDataChange]);

  const createBrowserSession = async () => {
    try {
      setErrorMessage("");
      setSessionStatus('creating');

      const token = await getToken();
      const response = await fetch(`${hostname}/browser-sessions/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          companyId,
          integrationType: 'linkedin_login',
          startUrl: 'https://www.linkedin.com/login',
          zipCode,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create browser session');
      }

      const data = await response.json();
      setSessionId(data.sessionId);
      setLiveUrl(data.liveUrl);
      // Store all session data for saving later (multi-instance compatibility)
      setSessionData({
        contextId: data.contextId,
        zipCode: data.zipCode,
        os: data.os,
        fingerprint: data.fingerprint,
        userAgent: data.userAgent,
        startUrl: data.startUrl,
      });
      setSessionStatus('ready');

    } catch (error: any) {
      console.error('Failed to create browser session:', error);
      setErrorMessage(error.message || 'Failed to create browser session');
      setSessionStatus('error');
    }
  };

  const saveBrowserSession = async () => {
    if (!sessionId) {
      setErrorMessage('No active session');
      return;
    }

    if (!email.trim()) {
      setErrorMessage('Please enter your LinkedIn email');
      return;
    }

    // Mark as success so parent dialog knows the session is ready to save
    // The actual save will happen when parent calls installLinkedInLogin
    setSessionStatus('success');
  };

  const cancelSession = async () => {
    // If we have an active session, tell the backend to shut it down
    if (sessionId) {
      try {
        const token = await getToken();
        await fetch(`${hostname}/browser-sessions/cancel/${sessionId}?companyId=${companyId}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // Don't need to check response - just best effort cleanup
      } catch (error) {
        console.error('Failed to cancel browser session on backend:', error);
        // Continue with local cleanup even if backend call fails
      }
    }

    // Reset local state
    setSessionId(null);
    setLiveUrl(null);
    setSessionStatus('idle');
    setErrorMessage("");
  };

  // Edit mode - just show integration info
  if (editMode) {
    return (
      <Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This LinkedIn account was connected via live browser session.
            To update the credentials, delete this integration and create a new one.
          </Typography>
        </Alert>

        <TextField
          fullWidth
          label="Email"
          value={email}
          disabled
          helperText="Email used for LinkedIn login"
          sx={{ mb: 2 }}
        />
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="bold" gutterBottom>
          Live Browser Session Login
        </Typography>
        <Typography variant="body2" gutterBottom>
          We'll open a live browser session where you can log in to LinkedIn yourself.
          Your credentials are never sent to our servers - you log in directly in the browser.
        </Typography>
        <Typography variant="body2">
          After logging in, click "Save Session" to complete the integration.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'bold' }}>
          Cost: 50 credits per browser task (includes residential proxy)
        </Typography>
      </Alert>

      {sessionStatus === 'idle' && (
        <>
          <TextField
            fullWidth
            type="email"
            label="LinkedIn Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            helperText="Your LinkedIn email address (for identification only)"
            sx={{ mb: 2 }}
            required
          />

          <TextField
            fullWidth
            label="ZIP Code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            helperText="Required for residential proxy to avoid LinkedIn detection"
            sx={{ mb: 2 }}
            required
          />

          <Button
            variant="contained"
            fullWidth
            onClick={createBrowserSession}
            disabled={!email || !zipCode}
            sx={{ mb: 2 }}
          >
            Start Live Browser Session
          </Button>
        </>
      )}

      {sessionStatus === 'creating' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
            Creating browser session...
          </Typography>
        </Box>
      )}

      {sessionStatus === 'ready' && liveUrl && (
        <Box>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight="bold" gutterBottom>
              Browser Session Ready!
            </Typography>
            <Typography variant="body2">
              Use the browser below to log in to LinkedIn. Once you're logged in, click "Save Session" below.
            </Typography>
          </Alert>

          {/* Iframe to show live browser session */}
          <Box
            sx={{
              mb: 3,
              border: '2px solid #ddd',
              borderRadius: 1,
              overflow: 'hidden',
              height: '500px',
            }}
          >
            <iframe
              src={/^https?:\/\/[a-zA-Z0-9]/.test(liveUrl) ? liveUrl : ""}
              sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
              allow="clipboard-read; clipboard-write"
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
              }}
              title="LinkedIn Login Browser"
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={saveBrowserSession}
            >
              Save Session
            </Button>

            <Button
              variant="outlined"
              onClick={cancelSession}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      )}

      {sessionStatus === 'success' && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="success">
            <Typography variant="body2">
              LinkedIn session saved successfully! Your integration is ready.
            </Typography>
          </Alert>
        </Box>
      )}

      {sessionStatus === 'error' && errorMessage && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {errorMessage}
            </Typography>
          </Alert>

          <Button
            variant="outlined"
            fullWidth
            onClick={cancelSession}
          >
            Try Again
          </Button>
        </Box>
      )}
    </Box>
  );
}
